import { useNavigate } from '@tanstack/react-router'
import { CtaButton } from '@workspace/ui/components/dsm/cta-button'
import { SecondaryButton } from '@workspace/ui/components/dsm/secondary-button'
import { useBasePath } from '../../../hooks/use-base-path'
import { useCopyToClipboard } from '../../../hooks/use-copy-to-clipboard'
import { ShareUrlLabel } from '../../../lib/share-url-label'
import { useSetDistributionIntentMutation } from '../../home/hooks/use-home-state'

export interface OnboardingLaneOverlayProps {
  startupId: string
  /** The startup's default share-link URL, or null while it is still loading. */
  shareUrl: string | null
  onDismiss: () => void
}

/**
 * One-time takeover shown the moment a startup's profile first becomes
 * shareable. Records the founder's distribution intent — the lane they click,
 * or SELF_SHARE if they skip — and is never shown again: `home-page.tsx` gates
 * mounting this on `distributionIntentPromptedAt` being null, and every path
 * out of here (lane click or skip) calls the same mutation, which stamps it.
 */
export function OnboardingLaneOverlay({
  startupId,
  shareUrl,
  onDismiss,
}: OnboardingLaneOverlayProps) {
  const navigate = useNavigate()
  const basePath = useBasePath()
  const setIntent = useSetDistributionIntentMutation(startupId)
  const { copied, copy } = useCopyToClipboard()

  const chooseShare = () => {
    copy(shareUrl)
    setIntent.mutate(
      { path: { startupId }, body: { intent: 'SELF_SHARE' } },
      { onSuccess: () => setTimeout(onDismiss, 350) }
    )
  }

  const chooseOutreach = () => {
    setIntent.mutate(
      { path: { startupId }, body: { intent: 'OUTREACH' } },
      {
        onSuccess: () => {
          onDismiss()
          navigate({ to: `${basePath}/outreach` as never })
        },
      }
    )
  }

  const skip = () => {
    setIntent.mutate(
      { path: { startupId }, body: { intent: 'SELF_SHARE' } },
      { onSuccess: () => onDismiss() }
    )
  }

  return (
    <div className="fixed inset-0 z-40 grid place-items-center overflow-auto bg-background p-10">
      <button
        type="button"
        onClick={skip}
        aria-label="Close"
        className="fixed top-4 right-5 grid size-8 place-items-center rounded-full border border-border text-muted-foreground"
      >
        ✕
      </button>
      <div className="w-full max-w-[600px] text-center">
        <p className="mb-3.5 text-[11px] font-bold tracking-[0.16em] text-primary uppercase">
          Your profile is live
        </p>
        <h2 className="mb-5 text-[32px] font-bold leading-tight tracking-tight">
          This link replaces
          <br />
          your deck attachment.
        </h2>

        <div className="mx-auto mb-3 flex max-w-[430px] flex-wrap items-center gap-2 rounded-lg border border-border bg-card px-3.5 py-2.5 text-left text-[14px]">
          {shareUrl ? (
            <ShareUrlLabel url={shareUrl} className="truncate" />
          ) : (
            <span className="truncate text-muted-foreground">Preparing your link…</span>
          )}
          <button
            type="button"
            onClick={() => copy(shareUrl)}
            disabled={!shareUrl}
            className="ml-auto rounded-md bg-primary px-3 py-1.5 text-[12.5px] font-bold text-primary-foreground disabled:opacity-50"
          >
            {copied ? 'Copied' : 'Copy link'}
          </button>
        </div>
        <p className="mb-6 text-[13px] text-muted-foreground">
          It always shows your latest profile. Send it instead of a PDF.
        </p>

        <div className="mb-5 grid gap-3 text-left sm:grid-cols-2">
          <div className="rounded-xl border border-border bg-card p-4">
            <b className="mb-1.5 block text-[15px]">Share it yourself</b>
            <p className="mb-3.5 text-[12.5px] text-muted-foreground">
              Send it to any investor you know. You get an email each time it is opened, and a log
              of who read what.
            </p>
            <SecondaryButton size="sm" loading={setIntent.isPending} onClick={chooseShare}>
              Copy link &amp; continue
            </SecondaryButton>
          </div>
          <div className="rounded-xl border border-border bg-card p-4">
            <b className="mb-1.5 block text-[15px]">Launch outreach</b>
            <p className="mb-3.5 text-[12.5px] text-muted-foreground">
              SeedForge matches funds by thesis and reaches them from your LinkedIn. You approve
              every message first.
            </p>
            <CtaButton size="sm" loading={setIntent.isPending} onClick={chooseOutreach}>
              Set up outreach
            </CtaButton>
          </div>
        </div>

        <button
          type="button"
          onClick={skip}
          className="text-[13px] text-muted-foreground underline underline-offset-2 hover:text-foreground"
        >
          Go to Home
        </button>
      </div>
    </div>
  )
}
